import { domain } from "../../core/Env";
import {
	PWA_SHELL_STORAGE,
	installPath,
	isShellInstallEnabled,
} from "./config";
import { isStandalone } from "./isStandalone";

const normalizePlatform = (platform) =>
	platform === "biz" || platform === "business" ? "biz" : "psl";

const readPending = (storage) => storage.getItem(PWA_SHELL_STORAGE.pendingUrl);

const readPlatform = (storage) =>
	storage.getItem(PWA_SHELL_STORAGE.pendingPlatform) || "psl";

export const getPendingToolUrl = () =>
	readPending(sessionStorage) || readPending(localStorage);

export const getPendingPlatform = () => {
	if (sessionStorage.getItem(PWA_SHELL_STORAGE.pendingUrl)) {
		return readPlatform(sessionStorage);
	}
	if (localStorage.getItem(PWA_SHELL_STORAGE.pendingUrl)) {
		return readPlatform(localStorage);
	}
	return "psl";
};

/** Arm resume-on-boot so a newly opened PWA at start_url "/" can pick up pending. */
export const armContinueOnce = () => {
	localStorage.setItem(PWA_SHELL_STORAGE.continueOnce, "1");
};

export const persistPendingToolUrl = (toolUrl, platform) => {
	const p = normalizePlatform(platform);
	sessionStorage.removeItem(PWA_SHELL_STORAGE.navLock);
	sessionStorage.setItem(PWA_SHELL_STORAGE.pendingUrl, toolUrl);
	sessionStorage.setItem(PWA_SHELL_STORAGE.pendingPlatform, p);
	// localStorage so the installed PWA window can read it after openWindow
	localStorage.setItem(PWA_SHELL_STORAGE.pendingUrl, toolUrl);
	localStorage.setItem(PWA_SHELL_STORAGE.pendingPlatform, p);
	// So Chrome opening the PWA at "/" after install can resume the callback.
	armContinueOnce();
};

export const clearPendingToolUrl = () => {
	sessionStorage.removeItem(PWA_SHELL_STORAGE.pendingUrl);
	sessionStorage.removeItem(PWA_SHELL_STORAGE.pendingPlatform);
	localStorage.removeItem(PWA_SHELL_STORAGE.pendingUrl);
	localStorage.removeItem(PWA_SHELL_STORAGE.pendingPlatform);
	localStorage.removeItem(PWA_SHELL_STORAGE.continueOnce);
};

export const markInstallSkipped = () => {
	sessionStorage.setItem(PWA_SHELL_STORAGE.skipSession, "1");
};

/** Arm when SSO/UBR starts so existing-user bounce to /login still skips install. */
export const armUbrSkipInstall = () => {
	if (typeof sessionStorage === "undefined") return;
	sessionStorage.setItem(PWA_SHELL_STORAGE.ubrSkipInstall, "1");
};

/** One-shot: true if UBR skip was armed; clears the flag. */
export const consumeUbrSkipInstall = () => {
	if (typeof sessionStorage === "undefined") return false;
	const armed = sessionStorage.getItem(PWA_SHELL_STORAGE.ubrSkipInstall) === "1";
	if (armed) {
		sessionStorage.removeItem(PWA_SHELL_STORAGE.ubrSkipInstall);
	}
	return armed;
};

export const markShellInstalled = () => {
	localStorage.setItem(
		PWA_SHELL_STORAGE.installed,
		JSON.stringify({ installed: true, at: new Date().toISOString() })
	);
};

const isShellOrHomePath = (pathname) => {
	const path = pathname || "";
	if (path === "/" || path === "") return true;
	return /^\/pwa\//.test(path);
};

/**
 * Persist the real tool/callback URL for later PWA reopen restore.
 * Only call immediately before navigating to that destination — never for / or /pwa/*.
 */
export const rememberLastDestination = (url) => {
	if (typeof window === "undefined" || !url || typeof url !== "string") {
		return;
	}

	let absolute = url.trim();
	if (!absolute) return;

	try {
		const parsed = new URL(absolute, window.location.origin);
		if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
			return;
		}
		if (isShellOrHomePath(parsed.pathname)) {
			return;
		}
		absolute = parsed.href;
	} catch (err) {
		return;
	}

	localStorage.setItem(PWA_SHELL_STORAGE.lastDestinationUrl, absolute);
};

/**
 * When the installed PWA reopens at start_url "/", restore the last real destination.
 * Runs after one-shot pending continue; does not clear the stored URL.
 */
export const resumeLastDestination = () => {
	if (typeof window === "undefined") return false;
	if (!isStandalone()) return false;
	if (sessionStorage.getItem(PWA_SHELL_STORAGE.navLock) === "1") {
		return false;
	}

	const path = window.location.pathname || "";
	const isContinueRoute = /\/pwa\/continue\/?$/.test(path);
	const isHome = path === "/" || path === "";
	if (!isContinueRoute && !isHome) return false;

	const last = localStorage.getItem(PWA_SHELL_STORAGE.lastDestinationUrl);
	if (!last) return false;

	try {
		const parsed = new URL(last);
		if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
			return false;
		}
		if (isShellOrHomePath(parsed.pathname) && parsed.origin === window.location.origin) {
			return false;
		}
		if (parsed.href === window.location.href) {
			return false;
		}
	} catch (err) {
		return false;
	}

	sessionStorage.setItem(PWA_SHELL_STORAGE.navLock, "1");
	window.location.replace(last);
	return true;
};

/** Stay in the current browser tab and go to the pending tool/callback URL. */
export const goToPendingOrFallback = (platform) => {
	// Prevent Strict Mode remount from clearing pending then falling back to home.
	if (sessionStorage.getItem(PWA_SHELL_STORAGE.navLock) === "1") {
		return;
	}

	const pending = getPendingToolUrl();
	const p = normalizePlatform(platform || getPendingPlatform());

	if (pending) {
		sessionStorage.setItem(PWA_SHELL_STORAGE.navLock, "1");
		clearPendingToolUrl();
		rememberLastDestination(pending);
		window.location.replace(pending);
		return;
	}

	const base = domain(p)?.url;
	window.location.replace(base || "/");
};

/**
 * When the installed PWA launches at start_url "/" (or lands on /pwa/continue),
 * pick up the pending tool URL that was stored before install/open.
 * Safe to call on every boot; no-ops unless continueOnce is set.
 */
export const resumePendingContinuation = () => {
	if (typeof window === "undefined") return false;
	if (localStorage.getItem(PWA_SHELL_STORAGE.continueOnce) !== "1") {
		return false;
	}
	if (sessionStorage.getItem(PWA_SHELL_STORAGE.navLock) === "1") {
		return false;
	}

	const path = window.location.pathname || "";
	const isContinueRoute = /\/pwa\/continue\/?$/.test(path);
	const isHome = path === "/" || path === "";
	// Do not steal the install page or mid-auth routes.
	if (!isContinueRoute && !isHome) return false;

	const pending = getPendingToolUrl();
	if (!pending) {
		localStorage.removeItem(PWA_SHELL_STORAGE.continueOnce);
		return false;
	}

	sessionStorage.setItem(PWA_SHELL_STORAGE.navLock, "1");
	clearPendingToolUrl();
	rememberLastDestination(pending);
	window.location.replace(pending);
	return true;
};

export const shouldOfferShellInstall = () => {
	if (!isShellInstallEnabled()) return false;
	if (isStandalone()) return false;
	if (sessionStorage.getItem(PWA_SHELL_STORAGE.skipSession) === "1") {
		return false;
	}
	// UBR (use-before-register) handoff — go straight to tool, no install gate.
	if (typeof window !== "undefined" && /\/ubr\//.test(window.location.pathname || "")) {
		return false;
	}
	return true;
};

/**
 * After auth success: either go straight to the tool, or gate through install page.
 * Always uses full navigation (safe for EntryAccess outside WebRoute).
 * Pass skipInstall: true to force tool redirect (e.g. UBR callers).
 * UBR SSO also arms ubrSkipInstall so existing-user /login handoff skips install.
 */
export const continueAfterAuth = (toolUrl, { platform, skipInstall = false } = {}) => {
	if (!toolUrl) {
		window.location.href = "/";
		return;
	}

	const p = normalizePlatform(platform);
	const ubrSkip = consumeUbrSkipInstall();

	if (skipInstall || ubrSkip || !shouldOfferShellInstall()) {
		rememberLastDestination(toolUrl);
		window.location.href = toolUrl;
		return;
	}

	persistPendingToolUrl(toolUrl, p);
	window.location.href = installPath();
};
